import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle2, XCircle, Loader2, Users, Trophy, Goal, Shield, Waves, Timer, Bike, Dumbbell, Medal } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Función para formatear RUT chileno
const formatRut = (value) => {
  let rut = value.replace(/[^0-9kK]/g, '').toUpperCase();
  if (rut.length === 0) return '';
  let dv = rut.slice(-1);
  let cuerpo = rut.slice(0, -1);
  let cuerpoFormateado = '';
  let contador = 0;
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    cuerpoFormateado = cuerpo[i] + cuerpoFormateado;
    contador++;
    if (contador === 3 && i !== 0) {
      cuerpoFormateado = '.' + cuerpoFormateado;
      contador = 0;
    }
  }
  if (cuerpo.length === 0) return dv;
  return cuerpoFormateado + '-' + dv;
};

// Formatear CPF brasileño
const formatCpf = (value) => {
  const cpf = value.replace(/[^0-9]/g, '');
  if (cpf.length <= 3) return cpf;
  if (cpf.length <= 6) return `${cpf.slice(0, 3)}.${cpf.slice(3)}`;
  if (cpf.length <= 9) return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6)}`;
  return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9, 11)}`;
};

// Formatear DNI español
const formatDniEspanol = (value) => {
  const dni = value.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
  if (dni.length <= 8) return dni;
  return `${dni.slice(0, 8)}-${dni.slice(8, 9)}`;
};

// Formatear documento según país
const formatDocumento = (value, pais) => {
  switch (pais) {
    case 'CL': return formatRut(value);
    case 'BR': return formatCpf(value);
    case 'ES': return formatDniEspanol(value);
    default: return value.replace(/[^0-9a-zA-Z-]/g, '').toUpperCase();
  }
};

// Validar RUT Chileno
const validarRutChileno = (rut) => {
  if (!rut) return { valido: false, mensaje: 'RUT requerido' };
  const rutLimpio = rut.replace(/[^0-9kK]/g, '').toUpperCase();
  if (rutLimpio.length < 8) return { valido: false, mensaje: 'RUT muy corto' };
  const cuerpo = rutLimpio.slice(0, -1);
  const dvIngresado = rutLimpio.slice(-1);
  let suma = 0;
  let multiplicador = 2;
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    suma += parseInt(cuerpo[i]) * multiplicador;
    multiplicador = multiplicador === 7 ? 2 : multiplicador + 1;
  }
  const resto = suma % 11;
  const dvCalculado = resto === 0 ? '0' : resto === 1 ? 'K' : String(11 - resto);
  if (dvIngresado === dvCalculado) return { valido: true, mensaje: 'RUT válido' };
  return { valido: false, mensaje: `Dígito verificador incorrecto (debería ser ${dvCalculado})` };
};

// Validar CPF Brasileño
const validarCpfBrasileno = (cpf) => {
  if (!cpf) return { valido: false, mensaje: 'CPF requerido' };
  const cpfLimpio = cpf.replace(/[^0-9]/g, '');
  if (cpfLimpio.length !== 11) return { valido: false, mensaje: 'CPF debe tener 11 dígitos' };
  if (/^(\d)\1+$/.test(cpfLimpio)) return { valido: false, mensaje: 'CPF inválido' };
  let suma = 0;
  for (let i = 0; i < 9; i++) suma += parseInt(cpfLimpio[i]) * (10 - i);
  let resto = (suma * 10) % 11;
  if (resto === 10) resto = 0;
  if (resto !== parseInt(cpfLimpio[9])) return { valido: false, mensaje: 'CPF inválido' };
  suma = 0;
  for (let i = 0; i < 10; i++) suma += parseInt(cpfLimpio[i]) * (11 - i);
  resto = (suma * 10) % 11;
  if (resto === 10) resto = 0;
  if (resto !== parseInt(cpfLimpio[10])) return { valido: false, mensaje: 'CPF inválido' };
  return { valido: true, mensaje: 'CPF válido' };
};

// Validar DNI Español
const validarDniEspanol = (dni) => {
  if (!dni) return { valido: false, mensaje: 'DNI requerido' };
  const dniLimpio = dni.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
  if (dniLimpio.length !== 9) return { valido: false, mensaje: 'DNI debe tener 8 dígitos y 1 letra' };
  const numero = dniLimpio.slice(0, 8);
  const letraIngresada = dniLimpio.slice(-1);
  if (!/^\d{8}$/.test(numero)) return { valido: false, mensaje: 'Formato incorrecto' };
  const letras = 'TRWAGMYFPDXBNJZSQVHLCKE';
  const letraCalculada = letras[parseInt(numero) % 23];
  if (letraIngresada === letraCalculada) return { valido: true, mensaje: 'DNI válido' };
  return { valido: false, mensaje: `Letra incorrecta (debería ser ${letraCalculada})` };
};

// Validar documento según país
const validarDocumento = (documento, pais, tipoDocumento) => {
  if (!documento) return { valido: false, mensaje: 'Documento requerido' };
  if (tipoDocumento === 'pasaporte') {
    if (documento.length >= 5) return { valido: true, mensaje: 'Pasaporte válido' };
    return { valido: false, mensaje: 'Pasaporte debe tener al menos 5 caracteres' };
  }
  switch (pais) {
    case 'CL': return validarRutChileno(documento);
    case 'BR': return validarCpfBrasileno(documento);
    case 'ES': return validarDniEspanol(documento);
    case 'AR':
      const dniAr = documento.replace(/[^0-9]/g, '');
      if (dniAr.length >= 7 && dniAr.length <= 8) return { valido: true, mensaje: 'DNI válido' };
      return { valido: false, mensaje: 'DNI debe tener 7 u 8 dígitos' };
    case 'PE':
      const dniPe = documento.replace(/[^0-9]/g, '');
      if (dniPe.length === 8) return { valido: true, mensaje: 'DNI válido' };
      return { valido: false, mensaje: 'DNI debe tener 8 dígitos' };
    default:
      if (documento.length >= 5) return { valido: true, mensaje: 'Documento válido' };
      return { valido: false, mensaje: 'Documento debe tener al menos 5 caracteres' };
  }
};

const DISCIPLINA_ICONS = {
  futbol: Goal,
  tenis: Trophy,
  basquetbol: Trophy,
  voleibol: Trophy,
  karate: Shield,
  natacion: Waves,
  atletismo: Timer,
  ciclismo: Bike,
  crossfit: Dumbbell,
  triathlon: Medal,
  otro: Trophy
};

export default function InscripcionPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    tipo_documento: 'documento',
    rut: '',
    fecha_nacimiento: '',
    email: '',
    telefono: '',
    direccion: ''
  });

  // Obtener nombre del documento según país
  const getNombreDocumento = () => {
    return info?.pais_config?.documento_principal || 'RUT';
  };

  useEffect(() => {
    loadInfo();
  }, [token]);

  const loadInfo = async () => {
    try {
      const res = await axios.get(`${API}/inscripcion/${token}`);
      setInfo(res.data);
    } catch (err) {
      setError('Este link de inscripción no es válido o ha sido desactivado.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre.trim() || !formData.apellido.trim() || !formData.rut.trim()) {
      toast.error('Por favor completa los campos obligatorios');
      return;
    }
    
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Por favor ingresa un correo electrónico válido');
      return;
    }

    // Validar documento
    const validacion = validarDocumento(formData.rut, info?.club?.pais || 'CL', formData.tipo_documento);
    if (!validacion.valido) {
      toast.error(validacion.mensaje);
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/inscripcion/${token}`, formData);
      setSuccess(true);
      toast.success('¡Solicitud enviada!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar la solicitud');
    } finally {
      setSubmitting(false);
    }
  };

  const DisciplinaIcon = info?.club?.disciplina ? (DISCIPLINA_ICONS[info.club.disciplina] || Trophy) : Users;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="p-8 bg-slate-800 border-slate-700 text-center">
          <Loader2 className="w-12 h-12 text-purple-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-300">Cargando información...</p>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-red-900/20 to-slate-900 flex items-center justify-center p-4">
        <Card className="p-8 bg-slate-800 border-slate-700 text-center max-w-md">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Link no válido</h2>
          <p className="text-slate-400 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} variant="outline" className="border-slate-600 text-slate-300">
            Ir al inicio
          </Button>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900/20 to-slate-900 flex items-center justify-center p-4">
        <Card className="p-8 bg-slate-800 border-slate-700 text-center max-w-md">
          <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">¡Solicitud Enviada!</h2>
          <p className="text-slate-400 mb-6">
            Tu solicitud de inscripción ha sido enviada correctamente. 
            El administrador del club la revisará y te contactará pronto.
          </p>
          <div className="flex flex-col gap-3">
            <Button onClick={() => navigate('/')} className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500">
              Conocer Kinosis
            </Button>
            <Button onClick={() => window.close()} variant="outline" className="w-full border-slate-600 text-slate-400 hover:text-slate-300">
              Cerrar
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 py-4 sm:py-8 px-3 sm:px-4">
      <div className="max-w-xl mx-auto">
        {/* Header del Club */}
        <Card className="p-4 sm:p-6 bg-slate-800 border-slate-700 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3 sm:gap-4 text-center sm:text-left">
            {info?.club?.logo ? (
              <img src={info.club.logo} alt="Logo" className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl object-cover" />
            ) : (
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl bg-purple-600/30 flex items-center justify-center">
                <DisciplinaIcon className="w-8 h-8 sm:w-10 sm:h-10 text-purple-400" />
              </div>
            )}
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-white">{info?.club?.nombre}</h1>
              <p className="text-purple-400 capitalize text-sm sm:text-base">{info?.club?.disciplina?.replace('_', ' ')}</p>
            </div>
          </div>
        </Card>

        {/* Info de Categoría */}
        <Card className="p-3 sm:p-4 bg-purple-600/20 border-purple-500/30 mb-4 sm:mb-6">
          <p className="text-xs sm:text-sm text-purple-300">Inscripción a la categoría:</p>
          <h2 className="text-lg sm:text-xl font-bold text-white">{info?.categoria?.nombre}</h2>
          {info?.categoria?.descripcion && (
            <p className="text-xs sm:text-sm text-slate-400 mt-1">{info.categoria.descripcion}</p>
          )}
          {(info?.categoria?.edad_minima || info?.categoria?.edad_maxima) && (
            <p className="text-xs text-purple-400 mt-2">
              Rango de edad: {info.categoria.edad_minima || '0'} - {info.categoria.edad_maxima || '99'} años
            </p>
          )}
        </Card>

        {/* Texto de Bienvenida del Club */}
        {info?.club?.texto_bienvenida && (
          <Card className="p-3 sm:p-4 bg-slate-800/50 border-slate-700 mb-4 sm:mb-6">
            <p className="text-slate-300 whitespace-pre-wrap text-sm sm:text-base">{info.club.texto_bienvenida}</p>
          </Card>
        )}

        {/* Formulario */}
        <Card className="p-4 sm:p-6 bg-slate-800 border-slate-700">
          <h3 className="text-base sm:text-lg font-semibold text-white mb-4">Completa tus datos</h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className="text-slate-300 text-sm">Nombre *</Label>
                <Input 
                  value={formData.nombre}
                  onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder="Juan"
                  required
                />
              </div>
              <div>
                <Label className="text-slate-300 text-sm">Apellido *</Label>
                <Input 
                  value={formData.apellido}
                  onChange={(e) => setFormData({...formData, apellido: e.target.value})}
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder="Pérez"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <div>
                <Label className="text-slate-300 text-sm">Tipo Documento <span className="text-red-400">*</span></Label>
                <select 
                  value={formData.tipo_documento}
                  onChange={(e) => setFormData({...formData, tipo_documento: e.target.value, rut: ''})}
                  className="w-full h-10 px-3 rounded-md bg-slate-700 border border-slate-600 text-white text-sm"
                >
                  <option value="documento">{getNombreDocumento()}</option>
                  <option value="pasaporte">Pasaporte</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-slate-300 text-sm">
                  {formData.tipo_documento === 'pasaporte' ? 'Pasaporte' : getNombreDocumento()} <span className="text-red-400">*</span>
                </Label>
                <Input 
                  value={formData.rut}
                  onChange={(e) => setFormData({
                    ...formData, 
                    rut: formData.tipo_documento === 'documento'
                      ? formatDocumento(e.target.value, info?.club?.pais || 'CL')
                      : e.target.value.toUpperCase()
                  })}
                  className={`bg-slate-700 border-slate-600 text-white ${formData.rut && !validarDocumento(formData.rut, info?.club?.pais || 'CL', formData.tipo_documento).valido ? 'border-red-500' : ''}`}
                  placeholder={formData.tipo_documento === 'pasaporte' ? 'AB123456' : '12.345.678-9'}
                  maxLength={formData.tipo_documento === 'pasaporte' ? 20 : 15}
                  required
                />
                {formData.rut && (
                  <p className={`text-xs mt-1 ${validarDocumento(formData.rut, info?.club?.pais || 'CL', formData.tipo_documento).valido ? 'text-green-400' : 'text-red-400'}`}>
                    {validarDocumento(formData.rut, info?.club?.pais || 'CL', formData.tipo_documento).mensaje}
                  </p>
                )}
              </div>
            </div>

            <div>
              <Label className="text-slate-300 text-sm">Fecha de Nacimiento</Label>
              <Input 
                type="date"
                value={formData.fecha_nacimiento}
                onChange={(e) => setFormData({...formData, fecha_nacimiento: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className="text-slate-300 text-sm">Email <span className="text-red-400">*</span></Label>
                <Input 
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder="tu@email.com"
                  required
                />
              </div>
              <div>
                <Label className="text-slate-300 text-sm">Teléfono</Label>
                <Input 
                  value={formData.telefono}
                  onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                  className="bg-slate-700 border-slate-600 text-white"
                  placeholder="+56 9 1234 5678"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-300 text-sm">Dirección</Label>
              <Input 
                value={formData.direccion}
                onChange={(e) => setFormData({...formData, direccion: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="Calle Principal 123"
              />
            </div>

            <div className="pt-4">
              <Button 
                type="submit" 
                disabled={submitting}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  'Enviar Solicitud de Inscripción'
                )}
              </Button>
              <p className="text-xs text-slate-500 text-center mt-3">
                Tu solicitud será revisada por el administrador del club
              </p>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
